/* src/pages/Home/index.jsx */

import React, { useRef } from "react";
import "./index.scss";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import CustomButton from "../../components/CustomButton";
import CustomDropdown from "../../components/CustomDropdown";
import InfoSection from "../../components/InfoSection";

// Icons & Images
import donationAmountIcon from "../../assets/icons/donation-automation.svg";
import customAllocationIcon from "../../assets/icons/custom-allocation.svg";
import splitDonationIcon from "../../assets/icons/split-donation.svg";
import globalReachIcon from "../../assets/icons/global-reach.svg";
import logo from "../../assets/images/Auto Giving Final Logo.png";
import step1Image from "../../assets/images/step1.png";
import step2Image from "../../assets/images/step2.png";
import step3Image from "../../assets/images/step3.png";
import step4Image from "../../assets/images/step4.jpeg";

// Framer
import { motion, useInView, useScroll, useTransform } from "framer-motion";
import {
  heroAnimations,
  keyFeaturesAnimations,
  howItworksVariants,
  featureStepVariants
} from "../../assets/framer/styles";

// Animations
import FloatingClouds from "../../components/FloatingClouds";
import ShootingStarsUpward from "../../components/ShootingStarsUpward";
import BirdAnimation from "../../components/BirdAnimation";

/* --------------------------------------------------------------------------------
   MAIN HOME COMPONENT
--------------------------------------------------------------------------------- */
const Home = () => {
  const navigate = useNavigate();
  return (
    <div>
      <HeroSection navigate={navigate} />
      <KeyFeatures navigate={navigate} />
      <HowItWorks />
      <StepsSection navigate={navigate} />
      <Footer navigate={navigate} />
    </div>
  );
};

/* --------------------------------------------------------------------------------
   HERO SECTION + LANGUAGE PICKER
--------------------------------------------------------------------------------- */
const HeroSection = ({ navigate }) => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const refParam = params.get("ref"); // Grab "ref" param if present

  // This builds the donate URL with optional "ref" parameter
  const buildUrl = (template) => {
    let url = `/donate?template=${template}`;
    if (refParam) {
      url += `&ref=${refParam}`;
    }
    return url;
  };

  // Language dropdown options (EN, FR)
  const languageOptions = [
    { label: "EN", value: "en" },
    { label: "FR", value: "fr" }
  ];

  // Find which option matches the current i18n language
  const currentLangOption =
    languageOptions.find((opt) => opt.value === i18n.language) ||
    languageOptions[0];

  // Called when user picks a new language
  const handleLanguageChange = (selectedOption) => {
    i18n.changeLanguage(selectedOption.value);
  };

  // Scroll-based animation
  const { scrollYProgress } = useScroll();
  const laptopY = useTransform(
    scrollYProgress,
    [0, 1],
    [0, window.innerWidth < 950 ? 1500 : 1000]
  );

  return (
    <motion.div
      className="hero-section"
      variants={heroAnimations.container}
      initial="hidden"
      animate="visible"
    >
      <FloatingClouds />
      <ShootingStarsUpward />

      <div className="hero-content">
        <motion.p
          className="hero-title"
          variants={heroAnimations.title}
          initial="hidden"
          animate="visible"
        >
          {t("heroSectionTitle")}
        </motion.p>

        <motion.p
          className="hero-description"
          variants={heroAnimations.description}
          initial="hidden"
          animate="visible"
        >
          {t("heroSectionDescription")}
        </motion.p>

        {/* 
          Language Picker Dropdown
          Adjust styling as desired (e.g., add margin-top, place it in a row, etc.)
        */}

        <motion.div
          variants={heroAnimations.button}
          initial="hidden"
          animate="visible"
          style={{
            gap: "1rem",
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 300px))",
            width: "100%",
            alignContent: "center",
            justifyContent: "center"
          }}
        >
          <CustomButton
            text={t("heroSectionBtnAutomate10Nights")}
            onClick={() => navigate(buildUrl("Friday"))}
            secondary
          />
        </motion.div>

        <motion.div
          className="laptop-container"
          variants={heroAnimations.image}
          initial="hidden"
          animate="visible"
          style={{ y: laptopY }}
        >
          <div className="laptop-screen">
            <motion.img
              src={require("../../assets/images/image.png")}
              alt="Hero Section"
              className="hero-image"
            />
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
};

/* --------------------------------------------------------------------------------
   KEY FEATURES
   (Note: useInView(...) returns a boolean, so we do `const inView = ...` not `[inView] = ...`
--------------------------------------------------------------------------------- */
const KeyFeatures = ({ navigate }) => {
  const { t } = useTranslation();

  // Outer container ref
  const outerRef = useRef(null);
  const outerInView = useInView(outerRef, { once: true, amount: 0.2 });

  // Feature 1
  const feature1Ref = useRef(null);
  const feature1InView = useInView(feature1Ref, { once: true, amount: 0.2 });

  // Feature 2
  const feature2Ref = useRef(null);
  const feature2InView = useInView(feature2Ref, { once: true, amount: 0.2 });

  // Feature 3
  const feature3Ref = useRef(null);
  const feature3InView = useInView(feature3Ref, { once: true, amount: 0.2 });

  // Feature 4
  const feature4Ref = useRef(null);
  const feature4InView = useInView(feature4Ref, { once: true, amount: 0.2 });

  // Build array
  const features = [
    {
      title: t("keyFeatures1Title"),
      description: t("keyFeatures1Description"),
      icon: donationAmountIcon,
      ref: feature1Ref,
      inView: feature1InView
    },
    {
      title: t("keyFeatures2Title"),
      description: t("keyFeatures2Description"),
      icon: customAllocationIcon,
      ref: feature2Ref,
      inView: feature2InView
    },
    {
      title: t("keyFeatures3Title"),
      description: t("keyFeatures3Description"),
      icon: splitDonationIcon,
      ref: feature3Ref,
      inView: feature3InView
    },
    {
      title: t("keyFeatures4Title"),
      description: t("keyFeatures4Description"),
      icon: globalReachIcon,
      ref: feature4Ref,
      inView: feature4InView
    }
  ];

  return (
    <motion.div
      className="key-features-section"
      ref={outerRef}
      initial="hidden"
      animate={outerInView ? "visible" : "hidden"}
      variants={keyFeaturesAnimations.container}
    >
      <BirdAnimation />

      <motion.div className="features-content">
        <motion.h2
          className="features-title"
          variants={keyFeaturesAnimations.title}
        >
          {t("keyFeaturesTitle")}
        </motion.h2>

        <motion.p
          className="features-description"
          variants={keyFeaturesAnimations.description}
        >
          {t("keyFeaturesDescription")}
        </motion.p>

        <div className="four-col-responsive-list-grid">
          {features.map((feature, index) => (
            <motion.div
              className="feature-card"
              key={index}
              ref={feature.ref}
              initial="hidden"
              whileHover="hover"
              animate={feature.inView ? "visible" : "hidden"}
              variants={keyFeaturesAnimations.card}
            >
              <div className="feature-icon">
                <div className="icon-wrapper">
                  <img
                    src={feature.icon}
                    alt={`${feature.title} icon`}
                    className="icon-image"
                  />
                </div>
              </div>

              <p className="feature-title">{feature.title}</p>
              <p className="feature-description">{feature.description}</p>
            </motion.div>
          ))}
        </div>

        <motion.div variants={keyFeaturesAnimations.button}>
          <CustomButton
            text={t("keyFeaturesBtnAutomateYourGiving")}
            onClick={() => navigate("/donate?template=Friday")}
            secondary
          />
        </motion.div>
      </motion.div>
    </motion.div>
  );
};

/* --------------------------------------------------------------------------------
   HOW IT WORKS
--------------------------------------------------------------------------------- */
const HowItWorks = () => {
  const { t } = useTranslation();

  const titleRef = useRef(null);
  const descriptionRef = useRef(null);

  // These return booleans, not arrays
  const titleInView = useInView(titleRef, { once: true, amount: 0.2 });
  const descriptionInView = useInView(descriptionRef, {
    once: true,
    amount: 0.2
  });

  return (
    <motion.div
      className="how-it-works-container"
      initial="hidden"
      animate="visible"
      variants={howItworksVariants.container}
    >
      <motion.div>
        <motion.h2
          className="how-it-works-title"
          ref={titleRef}
          initial="hidden"
          animate={titleInView ? "visible" : "hidden"}
          variants={howItworksVariants.title}
        >
          {t("howItWorksTitle")}
        </motion.h2>

        <motion.p
          className="how-it-works-description"
          ref={descriptionRef}
          initial="hidden"
          animate={descriptionInView ? "visible" : "hidden"}
          variants={howItworksVariants.description}
        >
          {t("howItWorksDescription")}
        </motion.p>
      </motion.div>
    </motion.div>
  );
};

/* --------------------------------------------------------------------------------
   STEPS SECTION
--------------------------------------------------------------------------------- */
const StepsSection = ({ navigate }) => {
  const { t } = useTranslation();

  // Step 1
  const step1Ref = useRef(null);
  const step1InView = useInView(step1Ref, { once: true, amount: 0.2 });

  // Step 2
  const step2Ref = useRef(null);
  const step2InView = useInView(step2Ref, { once: true, amount: 0.2 });

  // Step 3
  const step3Ref = useRef(null);
  const step3InView = useInView(step3Ref, { once: true, amount: 0.2 });

  // Step 4
  const step4Ref = useRef(null);
  const step4InView = useInView(step4Ref, { once: true, amount: 0.2 });

  // Build array of steps
  const steps = [
    {
      id: 1,
      image: step1Image,
      alt: "choose-cause-image",
      title: t("step1Title"),
      description: t("step1Description"),
      reverse: false,
      ref: step1Ref,
      inView: step1InView
    },
    {
      id: 2,
      image: step2Image,
      alt: "split-donation-image",
      title: t("step2Title"),
      description: t("step2Description"),
      reverse: true,
      ref: step2Ref,
      inView: step2InView
    },
    {
      id: 3,
      image: step3Image,
      alt: "choose-payment-image",
      title: t("step3Title"),
      description: t("step3Description"),
      reverse: false,
      ref: step3Ref,
      inView: step3InView
    },
    {
      id: 4,
      image: step4Image,
      alt: "confirm-image",
      title: t("step4Title"),
      description: t("step4Description"),
      reverse: true,
      ref: step4Ref,
      inView: step4InView
    }
  ];

  return (
    <div className="steps-section-container">
      <BirdAnimation />
      {steps.map((step) => (
        <motion.div
          key={step.id}
          ref={step.ref}
          className="choose-step-container"
          style={{ zIndex: 1 }}
          initial={featureStepVariants.hidden}
          animate={step.inView ? "visible" : "hidden"}
          variants={featureStepVariants}
        >
          <InfoSection
            image={
              <img src={step.image} alt={step.alt} className="step-image" />
            }
            content={
              <div className="content-step-content">
                <p className="content-step-title">{step.title}</p>
                <p className="content-step-description">{step.description}</p>
                <div className="content-step-button">
                  <CustomButton
                    text={t("letsDonate")}
                    onClick={() => navigate("/donate?template=Friday")}
                    secondary
                  />
                </div>
              </div>
            }
            reverse={step.reverse}
          />
        </motion.div>
      ))}
    </div>
  );
};

/* --------------------------------------------------------------------------------
   FOOTER
--------------------------------------------------------------------------------- */
const Footer = ({ navigate }) => {
  const { t } = useTranslation();

  return (
    <div className="footer">
      <div className="footer-content">
        <div className="footer-logo-section">
          <img src={logo} alt="PAC Logo" className="footer-logo" />

          <p className="footer-tagline">{t("footerTagline")}</p>

          <div className="social-media-section">
            <div
              className="social-icon"
              onClick={() =>
                window.open("https://www.facebook.com/pennyappealCA/", "_blank")
              }
            >
              <i className="fab fa-facebook-f"></i>
            </div>
            <div
              className="social-icon"
              onClick={() =>
                window.open("https://x.com/pennyappealca?lang=bn", "_blank")
              }
            >
              <i className="fab fa-twitter"></i>
            </div>
            <div
              className="social-icon"
              onClick={() =>
                window.open(
                  "https://www.linkedin.com/company/pennyappealca/posts/?feedView=all",
                  "_blank"
                )
              }
            >
              <i className="fab fa-linkedin-in"></i>
            </div>
            <div
              className="social-icon"
              onClick={() =>
                window.open(
                  "https://www.instagram.com/pennyappealca/?hl=en",
                  "_blank"
                )
              }
            >
              <i className="fab fa-instagram"></i>
            </div>
          </div>

          <p className="footer-copyright">{t("footerCopyright")}</p>
        </div>

        <div className="footer-links-section">
          <div className="footer-column">
            <h4 className="footer-heading">{t("footerInformation")}</h4>
            <ul className="footer-links">
              <li>{t("footerInformationAboutUs")}</li>
              <li>{t("footerInformationContactUs")}</li>
              <li>{t("footerInformationRegistration")}</li>
              <li>{t("footerInformationFees")}</li>
            </ul>
          </div>

          <div className="footer-column">
            <h4 className="footer-heading">{t("footerQuickLinks")}</h4>
            <ul className="footer-links">
              <li onClick={() => navigate("/")}>{t("footerQuickLinksHome")}</li>
              <li>{t("footerQuickLinksArticles")}</li>
              <li>{t("footerQuickLinksAboutEverGive")}</li>
              <li>{t("footerQuickLinksFAQ")}</li>
            </ul>
          </div>

          <div className="footer-column">
            <h4 className="footer-heading">{t("footerLegal")}</h4>
            <ul className="footer-links">
              <li>{t("footerLegalCookies")}</li>
              <li>{t("footerLegalPrivacy")}</li>
              <li>{t("footerLegalTerms")}</li>
            </ul>
          </div>
        </div>
      </div>

      <hr />
      <div className="footer-disclaimer">
        <p>{t("footerMadeByFikralabs")}</p>
      </div>
    </div>
  );
};

/* --------------------------------------------------------------------------------
   EXPORT HOME
--------------------------------------------------------------------------------- */
export default Home;
